import noOverlap, { getMaxIdxDFS } from './rbc/no-overlap';
import _groupBy from 'lodash-es/groupBy';
import { getSlotMetrics } from './rbc/TimeSlots';
import { EDTEvent } from '@teetsh/app/src/types/edt';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import endOfDay from '@teetsh/app/src/utils/time/endOfDay';

export function validateEdtEvent({ start, end }: { start: Date; end: Date }) {
  const difference = differenceInMinutes(end, start);
  if (difference <= 0) {
    return {
      end: "L'heure de fin doit être après celle de début",
    };
  }
  return {};
}

type EventWithIdxAndFriends = {
  event: EDTEvent;
  friends: EventWithIdxAndFriends[];
  idx: number;
};

export function getEventAdjacentFriends(
  movedEvent: EventWithIdxAndFriends,
  direction: 'left' | 'right',
): EventWithIdxAndFriends[] {
  if (!movedEvent.friends || movedEvent.friends.length === 0) {
    return [];
  }

  const targetIdx =
    direction === 'right' ? movedEvent.idx + 1 : movedEvent.idx - 1;
  // return adjacent friends that are in the given direction
  return movedEvent.friends.filter(friend => friend.idx === targetIdx);
}

export function reorderEvents(
  events: EDTEvent[],
  event: EDTEvent,
  direction: number,
  min: Date,
  max: Date,
  step: number,
) {
  const eventsWithIdx: EventWithIdxAndFriends[] = noOverlap({
    events,
    minimumStartDifference: 0,
    slotMetrics: getSlotMetrics({
      min,
      max: endOfDay(max),
      step,
      timeslots: 2,
    }),
    accessors: {
      start: e => e.start,
      end: e => e.end,
    },
  });

  const allFriends = [];
  getMaxIdxDFS(
    eventsWithIdx.find(e => e.event.id === event.id),
    0,
    allFriends,
  );

  const movedEventWithStyle = eventsWithIdx.find(e => e.event.id === event.id);

  if (!movedEventWithStyle) {
    return [];
  }

  const movementDirection = direction > 0 ? 'right' : 'left';
  const oppositeDirection = movementDirection === 'right' ? 'left' : 'right';
  const directionalFriends = getEventAdjacentFriends(
    movedEventWithStyle,
    movementDirection,
  );

  // we can do a simple swap if ALL adjacent friends in a given direction have the moved event as their only adjacent friend in the opposite direction
  // let's say we have A in column 1, B and C in column 2 next to A
  // if we move A to the right, we check that B and C have no other friends than A on their left
  const canDoSimpleSwap =
    directionalFriends.length > 0 &&
    directionalFriends.every(friend =>
      getEventAdjacentFriends(friend, oppositeDirection).every(
        f => f.event.id === event.id,
      ),
    );

  if (canDoSimpleSwap) {
    return [
      ...directionalFriends.map(friend => ({
        ...friend.event,
        order: movedEventWithStyle.idx + 1,
      })),
      {
        ...movedEventWithStyle.event,
        order: directionalFriends[0].idx + 1,
      },
    ];
  }

  const friendsIds = allFriends.map(e => (e as any)?.event?.id).filter(e => e);
  const impactedEvents = eventsWithIdx.filter(e =>
    friendsIds.includes(e.event.id),
  );

  if (!impactedEvents) {
    return [];
  }

  const eventToChangeIdx = movedEventWithStyle.idx;
  const groupedEvents = _groupBy(impactedEvents, e => e.idx);
  const nbOfColumns = Object.keys(groupedEvents).length;
  const newIndexForCurrent =
    eventToChangeIdx + direction === -1
      ? nbOfColumns - 1
      : (eventToChangeIdx + direction) % nbOfColumns;
  const newIndexForOld = eventToChangeIdx;

  return impactedEvents.map(eventWithStyle => {
    const idx = eventWithStyle.idx;
    if (idx === newIndexForCurrent) {
      return { ...eventWithStyle.event, order: newIndexForOld + 1 };
    }
    if (idx === newIndexForOld) {
      return { ...eventWithStyle.event, order: newIndexForCurrent + 1 };
    }
    return { ...eventWithStyle.event, order: idx + 1 };
  });
}
